import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Users, Check, X, Bell } from 'lucide-react';
import { useConsortiumInvitations } from '@/hooks/useConsortiumInvitations';

export const ConsortiumInvitations = () => {
  const { invitations, loading, respondToInvitation } = useConsortiumInvitations();
  const [responding, setResponding] = useState<string | null>(null);

  const handleResponse = async (invitationId: string, response: 'accepted' | 'declined') => {
    setResponding(invitationId);
    try {
      await respondToInvitation(invitationId, response);
    } finally {
      setResponding(null);
    }
  };

  if (loading || invitations.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
            {invitations.length}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />
            Invitations aux consortiums ({invitations.length})
          </h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {invitations.map((invitation) => (
            <Card key={invitation.id} className="m-2">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={invitation.inviter?.image} />
                    <AvatarFallback>
                      {invitation.inviter?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">{invitation.inviter?.name}</span> vous invite à rejoindre le consortium
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {invitation.consortium?.consortium_name || 'Consortium'} • {invitation.consortium?.opportunities?.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      chez {invitation.consortium?.opportunities?.company}
                    </p>
                    
                    {invitation.invitation_message && (
                      <p className="text-xs bg-muted p-2 rounded">
                        "{invitation.invitation_message}"
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleResponse(invitation.id, 'accepted')}
                    disabled={responding === invitation.id}
                    className="flex-1"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    {responding === invitation.id ? 'Accepter...' : 'Accepter'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleResponse(invitation.id, 'declined')}
                    disabled={responding === invitation.id}
                    className="flex-1"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Décliner
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};